<template>
  <header class="fixed top-0 left-0 w-full z-[1000] transition-all duration-500 ease-in-out" :class="{
    'py-4 bg-beige-light/90 backdrop-blur-md shadow-sm border-b border-umber/10': isScrolled,
    'py-8 bg-transparent border-b border-transparent': !isScrolled
  }" role="banner">
    <div class="max-w-[1200px] mx-auto flex justify-between items-center px-8">
      <RouterLink :to="{ name: 'HomePage' }" class="logo" aria-label="Retour à l'accueil">
        <span class="sr-only">Soulful Bloom par Dorothée Armbruster - Landes</span>
        <span class="font-cormorant text-2xl text-umber uppercase tracking-widest">
          D. Armbruster
        </span>
      </RouterLink>

      <nav class="hidden xl:flex items-center gap-10" role="navigation" aria-label="Navigation principale">
        <RouterLink :to="{ name: 'AboutPage' }" class="nav-link-base">
          Mon histoire
        </RouterLink>

        <div class="relative group">
          <button class="nav-link-base flex items-center gap-1" aria-haspopup="true" :aria-expanded="isDropdownOpen">
            Expériences
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
              class="transition-transform group-hover:rotate-180">
              <path d="m6 9 6 6 6-6" />
            </svg>
          </button>

          <div
            class="absolute top-full left-0 w-[max-content] bg-beige-light shadow-2xl border border-umber/5 py-4 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300"
            role="menu">
            <RouterLink :to="{ name: 'HealingPage' }" class="dropdown-link" role="menuitem">Expérience par le son
            </RouterLink>
            <RouterLink :to="{ name: 'BreathworkPage' }" class="dropdown-link" role="menuitem">Expérience par le souffle
            </RouterLink>
            <RouterLink :to="{ name: 'SoulfulBloomPage' }" class="dropdown-link" role="menuitem">Expérience Les
              Rencontres
            </RouterLink>
            <!-- <RouterLink :to="{ name: 'SecondSoufflePage' }" class="dropdown-link" role="menuitem">Retraite</RouterLink> -->
          </div>
        </div>

        <RouterLink :to="{ name: 'OnlinePage' }" class="nav-link-base">En ligne</RouterLink>
        <RouterLink :to="{ name: 'ProfessionalPage' }" class="nav-link-base">Professionnels</RouterLink>
        <RouterLink :to="{ name: 'PartnershipsPage' }" class="nav-link-base">Partenariats</RouterLink>
        <RouterLink :to="{ name: 'ContactPage' }" class="nav-link-base">Contact</RouterLink>
      </nav>

      <button class="flex flex-col gap-1.5 z-[1001] xl:hidden p-2" @click="isMenuOpen = !isMenuOpen"
        :aria-label="isMenuOpen ? 'Fermer le menu' : 'Ouvrir le menu'" :aria-expanded="isMenuOpen">
        <span class="block w-6 h-[0.5px] bg-umber transition-all duration-300"
          :class="{ 'translate-y-2 rotate-45': isMenuOpen }"></span>
        <span class="block w-6 h-[0.5px] bg-umber transition-all duration-300"
          :class="{ 'opacity-0': isMenuOpen }"></span>
        <span class="block w-6 h-[0.5px] bg-umber transition-all duration-300"
          :class="{ '-translate-y-2 -rotate-45': isMenuOpen }"></span>
      </button>
    </div>

    <transition enter-active-class="transition duration-500 ease-out" enter-from-class="opacity-0 translate-x-full"
      enter-to-class="opacity-100 translate-x-0" leave-active-class="transition duration-400 ease-in"
      leave-from-class="opacity-100 translate-x-0" leave-to-class="opacity-0 translate-x-full">
      <nav v-if="isMenuOpen"
        class="fixed inset-0 w-full h-screen bg-beige-light z-[1000] flex flex-col justify-center items-center gap-6 xl:hidden">
        <RouterLink v-for="link in mobileLinks" :key="link.label" :to="link.to" class="mobile-nav-link"
          @click="isMenuOpen = false">
          {{ link.label }}
        </RouterLink>

      </nav>
    </transition>
  </header>
</template>

<script setup lang='ts'>
import { ref, onMounted, onUnmounted } from 'vue';

// 1. États réactifs
const isMenuOpen = ref(false);
const isDropdownOpen = ref(false);
const isScrolled = ref(false);

const mobileLinks = [
  { label: 'Mon Histoire', to: { name: 'AboutPage' } },
  { label: 'Bain Sonore', to: { name: 'HealingPage' } },
  { label: 'Breathwork', to: { name: 'BreathworkPage' } },
  { label: 'Soulful Bloom', to: { name: 'SoulfulBloomPage' } },
  // { label: 'Retraite', to: { name: 'SecondSoufflePage' } },
  { label: 'En ligne', to: { name: 'OnlinePage' } },
  { label: 'Professionnels', to: { name: 'ProfessionalPage' } },
  { label: 'Partenariats', to: { name: 'PartnershipsPage' } },
  { label: 'Contact', to: { name: 'ContactPage' } },
];

const updateScroll = () => {
  isScrolled.value = window.scrollY > 50;
};

const handleReserveClick = () => {
  isMenuOpen.value = false;
};

onMounted(() => {
  window.addEventListener('scroll', updateScroll);
});

onUnmounted(() => {
  window.removeEventListener('scroll', updateScroll);
});
</script>

<style scoped>
.nav-link-base {
  @apply text-[10px] uppercase tracking-[0.2em] text-umber hover:text-gold-brush transition-colors duration-500;
}

.dropdown-link {
  @apply block px-8 py-3 text-[9px] uppercase tracking-widest text-umber/70 hover:bg-gold-brush hover:text-white transition-all duration-300;
}

.mobile-nav-link {
  @apply font-cormorant text-4xl text-umber hover:text-gold-brush transition-colors italic;
}
</style>